// The admin shell — the frame every /admin/* page renders inside.
// File: frontend/src/components/AdminShell.js
//
// AdminDashboard.js grew the "Soft Modern" look (soft-green field, left nav
// rail, white 18px-radius cards) but kept the sidebar private to itself, so
// every other admin page kept its own ad-hoc chrome: a bare max-width div,
// a stock table, and in several cases a #0066cc blue that appears nowhere in
// the brand. Moving the rail here and adding <AdminPage> lets a subpage adopt
// the dashboard frame in one wrapper instead of re-deriving it.
//
// Tokens live in AdminDashboard.css, scoped to `.adm` (see the note at the top
// of that file for why they are NOT on :root). That file is imported here so
// a page can pull in the shell alone and still get the variables; the
// subpage-only primitives (tables, tabs, filter bars) are in AdminShell.css.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { isAthlete } from '../utils/roles';
import '../pages/AdminDashboard.css';
import './AdminShell.css';

// Sidebar routes map to admin pages that already exist — a nav item that 404s
// is worse than one that isn't there yet. `payments` was held back for exactly
// that reason until Phase 5 landed /admin/payments (App.js), and is live now.
//
// `abbr` is what the rail shows once it collapses to 64px under 1100px. The
// dashboard hid the label there and these items carry no icons, so the
// collapsed rail was ten blank pills; see AdminShell.css.
//
// `roles` decides who sees an item. Partner entries are the 2026-09-15 partner
// spec's rail; Webinars/Messages/Lists are partner-only because partners used
// to reach them from the coach tabs, which the rail replaces.
const STAFF = ['admin', 'internal_staff'];
const STAFF_AND_PARTNER = ['admin', 'internal_staff', 'partner'];

// Who gets the left rail. Layout.js and the pages that branch on "rail or not" share this.
// The marketing role used to be a rail role, but its only items (the Dashboard / Schools / Players
// data-mining pages) left the rail on 2026-10-15, so it falls back to the plain layout rather than an
// empty rail.
export const usesRail = (role) => STAFF_AND_PARTNER.includes(role);

export const MENU = [
  { key: 'overview',  label: 'Overview',  abbr: 'OV', to: '/dashboard', roles: STAFF_AND_PARTNER, staffLabel: 'Home', partnerLabel: 'Home' },
  { key: 'search',    label: 'Search',    abbr: 'SR', to: '/players', roles: STAFF_AND_PARTNER },
  // Partners left this page for Athletes & Staff (/partner/team); App.js redirects them.
  { key: 'users',     label: 'Users',     abbr: 'US', to: '/admin/users', roles: STAFF },
  { key: 'merge',     label: 'Merge',     abbr: 'MG', to: '/admin/merge', roles: STAFF },
  { key: 'payments',  label: 'Payments',  abbr: 'PY', to: '/admin/payments', roles: STAFF },
  { key: 'reports',   label: 'Reports',   abbr: 'RP', to: '/admin/reports', roles: STAFF },
  // A partner's Stripe connection and concierge payment requests. Members are role 'partner' on the wire,
  // so they see it too; staff use the Payments page's Concierge tab instead.
  { key: 'billing',   label: 'Billing',   abbr: 'BL', to: '/partner/billing', roles: ['partner'] },
  // Partners see it as "Concierge" inside their Clients group (partner AI Cards handoff, 2026-11-05).
  { key: 'concierge', label: 'Clients', partnerLabel: 'Concierge', abbr: 'CG', to: '/concierge', roles: STAFF_AND_PARTNER },
  // A partner's own athletes' AI cards. Staff keep their review page (ai-cards, /admin/ai-cards).
  { key: 'partner-ai-cards', label: 'AI Cards', abbr: 'AC', to: '/partner/ai-cards', roles: ['partner'] },
  { key: 'coaches', label: 'College Contacts', abbr: 'CO', to: '/coaches', roles: STAFF_AND_PARTNER },
  // The route behind this arrives with the High School Contacts page (partner nav redesign, Task 5).
  { key: 'hs-coaches', label: 'High School Contacts', abbr: 'HS', to: '/coaches/high-school', roles: STAFF_AND_PARTNER },
  { key: 'meeting-notes', label: 'Meeting Note Exceptions', abbr: 'MN', to: '/admin/meeting-notes', roles: STAFF },
  { key: 'inquiries', label: 'Inquiries', abbr: 'IQ', to: '/admin/inquiries', roles: STAFF },
  { key: 'engage', label: 'Social Engage', abbr: 'SE', to: '/admin/engage', roles: ['admin'] },
  // One Lists entry for everyone with a rail: it replaced the staff List Builder and the partner's
  // old Lists (which pointed at /saved-athletes, a coach feature that is untouched).
  { key: 'lists',     label: 'Lists',     abbr: 'LS', to: '/lists', roles: STAFF_AND_PARTNER },
  { key: 'approvals', label: 'Approvals', abbr: 'AP', to: '/admin/approvals', badge: 'approvals', roles: STAFF },
  { key: 'flags',     label: 'Flags',     abbr: 'FL', to: '/admin/flags', badge: 'flags', roles: STAFF },
  { key: 'camps',     label: 'Camps',     abbr: 'CP', to: '/camps', roles: STAFF_AND_PARTNER },
  // Wristbands is no longer a rail item (David, 2026-10-29: "Wristbands is not an event"). It is a
  // header button on /camps for staff, and /admin/wristbands lights Camps.
  { key: 'content',   label: 'Content',   abbr: 'CN', to: '/admin/content', roles: STAFF },
  { key: 'school-logos', label: 'Logos', abbr: 'LG', to: '/admin/school-logos', roles: STAFF },
  { key: 'ai-cards', label: 'AI Cards', abbr: 'AI', to: '/admin/ai-cards', roles: STAFF },
  // Webinars, Messages and Drip campaigns used to be reachable only from the old top-nav dropdowns in
  // Layout.js (dead code now), so staff had no way in. They are rail items again (Admin home redesign).
  { key: 'webinars',  label: 'Webinars',  abbr: 'WB', to: '/webinars', roles: STAFF_AND_PARTNER },
  { key: 'messages',  label: 'Messages',  abbr: 'MS', to: '/messages', roles: STAFF_AND_PARTNER },
  { key: 'drip',      label: 'Drip campaigns', abbr: 'DR', to: '/admin/drip-campaigns', roles: STAFF },
  // The Marketing data-mining items (Dashboard / Schools / Players under /marketing/*) were removed
  // from the rail on 2026-10-15: their data service moved off this server. The pages and routes remain.
  // Reviewers are a flag on accounts of any role, so this item is not role-gated: `reviewerOnly` items
  // show for admins and for users with is_engage_reviewer, the same rule ReviewerRoute (App.js) and the
  // server enforce. menuFor(role) never returns them; build the rail with menuForUser(user).
  // The Campaigns report, moved from the Reports tab strip to Marketing (David, 2026-11-05). It is
  // still rendered by AdminReportsPage (?tab=campaigns), which lights this item instead of Reports.
  { key: 'campaigns', label: 'Campaigns', abbr: 'CA', to: '/admin/reports?tab=campaigns', roles: STAFF },
  { key: 'mkt-instagram', label: "Today's Instagram list", abbr: 'IG', to: '/engage/today', roles: [], reviewerOnly: true },
  // The partner's Athletes & Staff page. Members are role 'partner' on the wire and see it too (their
  // view of it is the page's concern); staff never do.
  { key: 'team', label: 'Athletes & Staff', abbr: 'TM', to: '/partner/team', roles: ['partner'] },
];

export const menuFor = (role) => MENU.filter((m) => m.roles.includes(role));

export const canReviewEngage = (user) => !!user && (user.role === 'admin' || !!user.is_engage_reviewer);

// The rail for this user: their role's items, plus the reviewer-only items when they can open them.
export const menuForUser = (user) => MENU.filter((m) => {
  if (m.reviewerOnly) return canReviewEngage(user);
  return m.roles.includes(user?.role);
});

// Keys are MENU keys. Staff render through STAFF_GROUPS, partners through PARTNER_GROUPS below; `team` is
// partner-only (roles ['partner']), so staff never see it.
// The staff rail: Home on its own, then the groups, one open at a time (Admin home redesign, 2026-10-13).
// Group order is David's, 2026-10-29: Home, Business, People, Recruiting, Events, Messaging,
// Marketing, Site. (It was Home/Business/Events/People/Recruiting/Messaging/Site/Marketing from
// 2026-10-24.)
// Partner rail order: David, 2026-11-05 (nav handoff): Home, Business, My team, Clients, Recruiting,
// Events, Messaging. Partners' Users ("My athletes") left the rail for Athletes & Staff under My team.
export const STAFF_GROUPS = [
  { label: 'Business', keys: ['payments', 'reports', 'billing'] },
  // AI Cards under People, Inquiries under Site, Campaigns under Marketing: David, 2026-11-05.
  { label: 'People', keys: ['users', 'approvals', 'merge', 'ai-cards'] },
  // Recruiting order and Meeting Note Exceptions under Site: David, 2026-10-30.
  { label: 'Recruiting', keys: ['concierge', 'coaches', 'hs-coaches', 'lists', 'search'] },
  { label: 'Events', keys: ['camps', 'webinars'] },
  { label: 'Messaging', keys: ['messages', 'flags', 'drip', 'engage'] },
  // Campaigns is staff-wide; the Instagram list is admins and flagged reviewers only. An empty group
  // is not drawn.
  { label: 'Marketing', keys: ['campaigns', 'mkt-instagram'] },
  { label: 'Site', keys: ['content', 'school-logos', 'inquiries', 'meeting-notes'] },
];

// The partner rail. Marketing is only ever drawn for a flagged reviewer, as on the staff rail.
export const PARTNER_GROUPS = [
  { label: 'Business', keys: ['billing'] },
  { label: 'My team', keys: ['team', 'coaches', 'hs-coaches'] },
  // Clients group: Concierge + AI Cards (partner AI Cards handoff, 2026-11-05). Concierge used to be a
  // standalone "Clients" link here.
  { label: 'Clients', keys: ['concierge', 'partner-ai-cards'] },
  { label: 'Recruiting', keys: ['lists', 'search'] },
  { label: 'Events', keys: ['camps', 'webinars'] },
  { label: 'Messaging', keys: ['messages'] },
  { label: 'Marketing', keys: ['mkt-instagram'] },
];
// Items drawn as plain links rather than inside a group. Overview is first; any other would sit after
// the group named in PARTNER_STANDALONE_AFTER (none since Concierge moved into Clients, 2026-11-05).
export const PARTNER_STANDALONE = ['overview'];
const PARTNER_STANDALONE_AFTER = {};

const BADGE_TONE = { approvals: 'amber', flags: 'red' };
const groupOf = (groups, key) => (groups.find((g) => g.keys.includes(key)) || {}).label || null;

export function AdminSidebar({ active, approvalsCount, flagsCount }) {
  const { user } = useAuth();
  const isPartner = user?.role === 'partner';
  const activeKey = active;

  const groups = isPartner ? PARTNER_GROUPS : STAFF_GROUPS;
  const [openGroup, setOpenGroup] = useState(groupOf(groups, active));
  // Landing on a page opens the group that contains it.
  useEffect(() => { setOpenGroup(groupOf(groups, active)); }, [active, groups]);

  const renderItem = (item) => {
    const count = item.badge === 'approvals' ? approvalsCount
                : item.badge === 'flags' ? flagsCount
                : null;
    const label = (isPartner && item.partnerLabel) || (!isPartner && item.staffLabel) || item.label;
    return (
      <Link
        key={item.key}
        to={item.to}
        className={`adm-sidebar__item ${activeKey === item.key ? 'adm-sidebar__item--active' : ''}`}
        aria-current={activeKey === item.key ? 'page' : undefined}
      >
        <span className="adm-sidebar__text">{label}</span>
        <span className="adm-sidebar__abbr" aria-hidden="true">{item.abbr}</span>
        {count > 0 && (
          <span className={`adm-sidebar__badge adm-sidebar__badge--${item.badge}`}>
            {count}
          </span>
        )}
      </Link>
    );
  };

  const items = menuForUser(user);
  // Nothing to show (e.g. an unflagged marketing account on a page that still uses <AdminPage>): no
  // empty rail.
  if (!items.length) return null;
  const byKey = Object.fromEntries(items.map((m) => [m.key, m]));
  const counts = { approvals: approvalsCount || 0, flags: flagsCount || 0 };
  return (
    <nav className="adm-sidebar" aria-label={isPartner ? 'Partner sections' : 'Admin sections'}>
      {byKey.overview && renderItem(byKey.overview)}
      {groups.map((group) => {
        const children = group.keys.map((k) => byKey[k]).filter(Boolean);
        if (!children.length) return null;
        const open = openGroup === group.label;
        // A collapsed group rolls its children's badges up: red if any red child has a count, else amber.
        const withCount = children.filter((c) => c.badge && counts[c.badge] > 0);
        const rollup = withCount.reduce((n, c) => n + counts[c.badge], 0);
        const tone = withCount.some((c) => BADGE_TONE[c.badge] === 'red') ? 'red' : 'amber';
        return (
          <React.Fragment key={group.label}>
          <div className="adm-sidebar__group-wrap">
            <button type="button" className={`adm-sidebar__group-btn ${open ? 'adm-sidebar__group-btn--open' : ''}`}
              aria-expanded={open} onClick={() => setOpenGroup(open ? null : group.label)}>
              <span>{group.label}</span>
              {!open && rollup > 0 && <span className={`adm-sidebar__rollup adm-sidebar__rollup--${tone}`}>{rollup}</span>}
              <span className="adm-sidebar__chevron" aria-hidden="true">▶</span>
            </button>
            {/* Hidden with a CSS class, not unmounted, so a collapsed group's links stay in the page. */}
            <div className={`adm-sidebar__children ${open ? '' : 'adm-sidebar__children--collapsed'}`}>
              {children.map(renderItem)}
            </div>
          </div>
          {isPartner && PARTNER_STANDALONE.filter((k) => PARTNER_STANDALONE_AFTER[k] === group.label && byKey[k])
            .map((k) => renderItem(byKey[k]))}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

// The dashboard already fetches these two counts for its own cards and passes
// them down. A subpage has no such fetch, so the shell gets them itself —
// swallowing failures, because a badge that cannot load must not take the page
// with it. Same defensive shape as AdminDashboard's `safe()`.
export function useAdminBadges(enabled = true) {
  const { API_URL } = useAuth();
  // Derived from API_URL per the repo's convention (AdminDashboard.js:21-23,
  // Layout.js:31), not a separate REACT_APP_* env var.
  const MESSAGING_URL = `${API_URL.replace('/api', '')}/messaging`;
  const [badges, setBadges] = useState({ approvals: 0, flags: 0 });

  useEffect(() => {
    // Both endpoints are staff-only — a partner calling them would 403.
    // Hook order must stay legal for React, so this returns early inside the
    // effect rather than skipping the useEffect call itself.
    if (!enabled) return undefined;
    let cancelled = false;
    const safe = (p) => p.then((r) => r.data).catch(() => null);
    Promise.all([
      safe(axios.get(`${API_URL}/admin/coach-review/pending`)),
      safe(axios.get(`${MESSAGING_URL}/flags/count`)),
    ]).then(([pending, flags]) => {
      if (cancelled) return;
      setBadges({
        approvals: Array.isArray(pending) ? pending.length : 0,
        flags: flags?.pending_flags || 0,
      });
    });
    return () => { cancelled = true; };
  }, [API_URL, MESSAGING_URL, enabled]);

  return badges;
}

/**
 * One-line trail above a detail page's H1: `Camps › Dallas Combine › Check-in`.
 * Each crumb with a `to` links to its parent route; the last crumb is plain text.
 */
function Breadcrumb({ items }) {
  return (
    <nav className="adm-crumbs" aria-label="Breadcrumb">
      {items.map((c, i) => {
        const last = i === items.length - 1;
        return (
          <React.Fragment key={`${c.label}-${i}`}>
            {c.to && !last
              ? <Link to={c.to} className="adm-crumbs__link">{c.label}</Link>
              : <span className={last ? 'adm-crumbs__last' : undefined} aria-current={last ? 'page' : undefined}>{c.label}</span>}
            {!last && <span className="adm-crumbs__sep" aria-hidden="true">›</span>}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

/**
 * Put an existing page inside the rail WITHOUT touching its content, and only for rail roles.
 *
 * Several routes are shared with coaches, athletes and guardians (/athletes/:id, /saved-athletes,
 * /camps, /settings ...). Those roles have no rail, so for them this returns `children` untouched
 * and their page is byte-for-byte what it was. For admin / staff / partner it renders
 * the same frame <AdminPage> does, minus the header (the page keeps its own).
 *
 * `active` is a MENU key, or null to show the rail with nothing lit (avatar-menu pages).
 */
export function RailFrame({ active, children }) {
  const { user } = useAuth();
  const isStaff = user?.role === 'admin' || user?.role === 'internal_staff';
  // Hook runs for every role; it only fetches when `enabled`.
  const { approvals, flags } = useAdminBadges(isStaff);
  if (!usesRail(user?.role)) return <>{children}</>;
  return (
    <div className="adm">
      <div className="adm__shell">
        <AdminSidebar active={active} approvalsCount={approvals} flagsCount={flags} />
        <main className="adm__main">{children}</main>
      </div>
    </div>
  );
}

/**
 * The frame for an admin subpage.
 *
 * <AdminPage active="reports" title="Reports" subtitle="…" actions={<…/>}>
 *   <div className="adm__card">…</div>
 * </AdminPage>
 *
 * `eyebrow` is an optional small uppercase line above the title ("MY TEAM").
 *
 * `active` is a MENU key. Pages that are a detail view of a menu item pass
 * that item's key — /admin/signups and /admin/logins are both Reports tabs,
 * so they light "Reports" rather than going unhighlighted.
 *
 * `actions` renders top-right of the header, where the dashboard puts its
 * window switcher; pass a <AdminWindowSwitcher> or a button there.
 */
export default function AdminPage({ active, title, eyebrow, subtitle, actions, breadcrumb, children }) {
  const { user } = useAuth();
  const isStaff = user?.role === 'admin' || user?.role === 'internal_staff';
  // Approvals/flags counts come from staff-only endpoints; a partner would 403.
  const { approvals, flags } = useAdminBadges(isStaff);
  return (
    <div className="adm">
      <div className="adm__shell">
        <AdminSidebar active={active} approvalsCount={approvals} flagsCount={flags} />
        <main className="adm__main">
          {breadcrumb && breadcrumb.length > 0 && <Breadcrumb items={breadcrumb} />}
          <header className="adm-header">
            <div>
              {eyebrow && <div className="adm-header__eyebrow">{eyebrow}</div>}
              <h1 className="adm-header__title">{title}</h1>
              {subtitle && <p className="adm-header__sub">{subtitle}</p>}
            </div>
            {actions}
          </header>
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * The dashboard's pill window switcher, generalized.
 *
 * The dashboard's own copy (AdminDashboard.js) is keyed by `{key,label}` over
 * a fixed 7d/30d/90d list. Subpages switch over day counts (1/7/30) and one
 * switches over roles, so this takes the option list as a prop. Same markup
 * and the same .adm-window classes — one control, not a second one that
 * merely looks similar.
 */
export function AdminWindowSwitcher({ options, value, onChange, label = 'Time window' }) {
  return (
    <div className="adm-window" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`adm-window__btn ${value === o.value ? 'adm-window__btn--active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Where clicking a person in an admin list goes.
 *
 *   'profile'  -> /athletes/{id}, the canonical full-profile route
 *   'guardian' -> open GuardianLinksModal on their linked athlete(s)
 *   null       -> not clickable
 *
 * Only athletes have an AthleteProfile, so only they have a profile page.
 * A guardian is still worth following up on but /athletes/{guardianId}
 * renders "Profile not found", so their linked athletes are the destination
 * instead. Coach / partner / staff rows have neither, and used to be rendered
 * as links anyway on the signups and logins reports — a green underlined name
 * that landed on an error page.
 *
 * `isAthlete` covers all three athlete role values, which matters because the
 * two callers see DIFFERENT ones: /admin/reports/signups coalesces through
 * coalesce_role() and emits "athlete", while /admin/users returns the raw
 * "player_parent" / "base_user".
 */
export function personTarget(role) {
  if (isAthlete(role)) return 'profile';
  if (role === 'guardian') return 'guardian';
  return null;
}
